import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { API_ENDPOINTS } from '../config/api'
import { downloadExport } from '../utils/downloadExport'
import { PageLayout, PageHeader, Card } from '../components/ui'
import ImpactStatsRow from '../components/impact/ImpactStatsRow'
import ImpactQuestionTable from '../components/impact/ImpactQuestionTable'
import ImpactComparisonBars from '../components/impact/ImpactComparisonBars'
import type { ImpactReport } from '../types'

interface Survey {
  id: string
  title: string
  status: string
  organization_id: string
}


const ImpactReportPage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId?: string }>()
  const intl = useIntl()
  const { locale } = useLanguage()
  useAuth()
  const [surveys, setSurveys] = useState<Survey[]>([])
  const [selectedSurveyId, setSelectedSurveyId] = useState(surveyId || '')
  const [report, setReport] = useState<ImpactReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingReport, setLoadingReport] = useState(false)
  const [exporting, setExporting] = useState<'pdf' | 'csv' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token')
    return { 'Authorization': token ? `Bearer ${token}` : '' }
  }

  const handleExport = async (kind: 'pdf' | 'csv') => {
    if (!report) return
    setExporting(kind)
    try {
      const url = kind === 'pdf'
        ? API_ENDPOINTS.impact.exportPdf(report.survey_id, locale)
        : API_ENDPOINTS.impact.exportCsv(report.survey_id, locale)
      await downloadExport(url, `${report.survey_title || 'impact-report'}.${kind}`)
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setExporting(null)
    }
  }

  useEffect(() => {
    const loadSurveys = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.surveys.base, { headers: getAuthHeaders() })
        if (res.ok) {
          const data = await res.json()
          setSurveys(Array.isArray(data) ? data : data.surveys || [])
        }
      } catch (err) {
        console.error('Failed to load surveys:', err)
      } finally {
        setLoading(false)
      }
    }
    loadSurveys()
  }, [])

  // Auto-load report when surveyId comes from URL params
  useEffect(() => {
    if (surveyId) {
      setSelectedSurveyId(surveyId)
      loadReport(surveyId)
    }
  }, [surveyId])

  const loadReport = async (id: string) => {
    setLoadingReport(true)
    setError(null)
    setReport(null)
    try {
      const res = await fetch(API_ENDPOINTS.impact.report(id), { headers: getAuthHeaders() })
      if (!res.ok) {
        const err = await res.json()
        setError(err.detail || intl.formatMessage({ id: 'impact.failedLoad' }))
        return
      }
      setReport(await res.json())
    } catch (err) {
      console.error('Failed to load impact report:', err)
      setError(intl.formatMessage({ id: 'impact.failedLoad' }))
    } finally {
      setLoadingReport(false)
    }
  }

  const handleViewReport = () => {
    if (selectedSurveyId) loadReport(selectedSurveyId)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      </div>
    )
  }

  return (
    <PageLayout>
      <PageHeader
        title={intl.formatMessage({ id: 'impact.title' })}
        subtitle={intl.formatMessage({ id: 'impact.subtitle' })}
        backTo="/dashboard"
        backLabel={intl.formatMessage({ id: 'impact.backToDashboard' })}
      />

      {/* Survey Picker */}
      <Card elevated className="mb-8">
        <h3 className="text-lg font-semibold text-foreground mb-4"><FormattedMessage id="impact.selectSurvey" /></h3>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground-muted mb-1"><FormattedMessage id="impact.survey" /></label>
            <select
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              value={selectedSurveyId}
              onChange={e => setSelectedSurveyId(e.target.value)}
            >
              <option value="">{intl.formatMessage({ id: 'impact.selectPlaceholder' })}</option>
              {surveys.map(s => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          </div>
          <button
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!selectedSurveyId || loadingReport}
            onClick={handleViewReport}
          >
            {loadingReport
              ? intl.formatMessage({ id: 'impact.loading' })
              : intl.formatMessage({ id: 'impact.viewReport' })}
          </button>
        </div>
        {error && (
          <p className="mt-3 text-sm text-error">{error}</p>
        )}
      </Card>

      {/* Report */}
      {report && (
        <>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <h2 className="text-2xl font-semibold text-foreground">{report.survey_title}</h2>
            <div className="flex items-center gap-3">
              <button
                className="px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-background disabled:opacity-50"
                disabled={exporting !== null}
                onClick={() => handleExport('pdf')}
              >
                {exporting === 'pdf' ? intl.formatMessage({ id: 'impact.loading' }) : intl.formatMessage({ id: 'impact.exportPdf' })}
              </button>
              <button
                className="px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-background disabled:opacity-50"
                disabled={exporting !== null}
                onClick={() => handleExport('csv')}
              >
                {exporting === 'csv' ? intl.formatMessage({ id: 'impact.loading' }) : intl.formatMessage({ id: 'impact.exportCsv' })}
              </button>
              <button
                className="text-sm text-primary hover:text-primary-dark"
                onClick={() => loadReport(report.survey_id)}
              >
                <FormattedMessage id="impact.refresh" />
              </button>
            </div>
          </div>

          <ImpactStatsRow report={report} />

          {report.respondents_with_multiple_attempts === 0 ? (
            <Card elevated className="mb-8">
              <div className="text-center py-8">
                <p className="text-foreground-muted text-sm">
                  <FormattedMessage id="impact.noMultiAttempt" />
                </p>
              </div>
            </Card>
          ) : (
            <>
              {/* Per-Question Table */}
              {report.per_question.length > 0 && (
                <>
                  <h2 className="text-xl font-semibold text-foreground mb-4"><FormattedMessage id="impact.perQuestion" /></h2>
                  <ImpactQuestionTable report={report} className="mb-8" />
                </>
              )}

              {/* Visual bars */}
              {report.per_question.length > 0 && (
                <>
                  <h2 className="text-xl font-semibold text-foreground mb-4"><FormattedMessage id="impact.visualComparison" /></h2>
                  <ImpactComparisonBars report={report} className="mb-8" />
                </>
              )}
            </>
          )}

          {/* Summary */}
          <h2 className="text-xl font-semibold text-foreground mb-4"><FormattedMessage id="impact.summary" /></h2>
          <Card elevated className="mb-8">
            <p className="text-foreground leading-relaxed">{report.summary}</p>
            <p className="text-xs text-foreground-muted mt-3">
              <FormattedMessage id="impact.computedAt" values={{ date: new Date(report.computed_at).toLocaleString(locale) }} />
            </p>
          </Card>
        </>
      )}
    </PageLayout>
  )
}

export default ImpactReportPage
